import { useState, useEffect } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { api } from '../api';

const LEVEL_COLOURS: Record<number, string> = {
  1: 'bg-green-100 text-green-800 border-green-300',
  2: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  3: 'bg-orange-100 text-orange-800 border-orange-300',
  4: 'bg-red-100 text-red-800 border-red-300',
  5: 'bg-red-200 text-red-900 border-red-400',
  6: 'bg-red-300 text-red-950 border-red-500',
};

const PROC_STATUS_COLOURS: Record<string, string> = {
  outstanding: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

type Tab = 'drivers' | 'procedures' | 'letters' | 'config';

export function CompliancePage() {
  const [tab, setTab] = useState<Tab>('drivers');
  const [drivers, setDrivers] = useState<any[]>([]);
  const [procedures, setProcedures] = useState<any[]>([]);
  const [letters, setLetters] = useState<any[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [letterDriver, setLetterDriver] = useState<number | null>(null);
  const [letterPreview, setLetterPreview] = useState<any>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [d, p, l, c] = await Promise.all([
        api.getDCRSDrivers(),
        api.getDCRSProcedures(),
        api.getLetters(),
        api.getDCRSConfig(),
      ]);
      setDrivers(d);
      setProcedures(p);
      setLetters(l);
      setConfig(c);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleProcedureAction(id: number, status: string) {
    await api.updateDCRSProcedure(id, { status });
    const p = await api.getDCRSProcedures();
    setProcedures(p);
  }

  async function handleGenerateLetter(driverId: number) {
    setLetterDriver(driverId);
    try {
      const preview = await api.autoGenerateLetter(driverId);
      setLetterPreview(preview);
      setShowLetterModal(true);
    } catch (err: any) {
      alert(err.message);
    }
  }

  async function handleSendLetter() {
    if (!letterPreview || !letterDriver) return;
    try {
      await api.createLetter({
        driver_id: letterDriver,
        period_start: letterPreview.period_start,
        period_end: letterPreview.period_end,
        infringements: letterPreview.infringements,
        send_email: true,
      });
      setShowLetterModal(false);
      setLetterPreview(null);
      const l = await api.getLetters();
      setLetters(l);
      alert('Infringement letter sent successfully');
    } catch (err: any) {
      alert(err.message);
    }
  }

  const outstandingProcs = procedures.filter(p => p.status === 'outstanding').length;
  const overdueLetters = letters.filter(l => l.status === 'overdue').length;

  const TABS: { id: Tab; label: string; badge?: number }[] = [
    { id: 'drivers', label: 'Driver Levels' },
    { id: 'procedures', label: 'Procedures', badge: outstandingProcs || undefined },
    { id: 'letters', label: 'Infringement Letters', badge: overdueLetters || undefined },
    { id: 'config', label: 'DCRS Settings' },
  ];

  return (
    <PortalLayout currentPage="compliance">
      <div className="p-5">
        <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900 mb-1">Compliance & DCRS</h1>
        <p className="text-sm text-gray-500 mb-6">Driver Compliance Reporting System — manage infringements, letters, and procedures</p>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded p-1 w-fit">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.badge && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{t.badge}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading compliance data...</div>
        ) : (
          <>
            {/* Driver Levels Tab */}
            {tab === 'drivers' && (
              <div className="space-y-3">
                {drivers.map((d: any) => {
                  const levelClass = LEVEL_COLOURS[d.current_level] || 'bg-gray-100 text-gray-600';
                  return (
                    <div key={d.id} className="bg-white border border-gray-200 rounded p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded flex items-center justify-center font-bold text-lg border ${levelClass}`}>
                          L{d.current_level}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{d.driver_name}</p>
                          <p className="text-xs text-gray-500">
                            {d.total_points} total points &bull; {d.points_at_current_level} at current level
                            {d.last_infringement_date && <> &bull; Last infringement: {d.last_infringement_date}</>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleGenerateLetter(d.driver_id)}
                          className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        >
                          Generate Letter
                        </button>
                      </div>
                    </div>
                  );
                })}
                {drivers.length === 0 && <p className="text-gray-400 text-center py-8">No drivers found</p>}
              </div>
            )}

            {/* Procedures Tab */}
            {tab === 'procedures' && (
              <div className="space-y-3">
                {procedures.map((p: any) => (
                  <div key={p.id} className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PROC_STATUS_COLOURS[p.status] || ''}`}>
                            {p.status}
                          </span>
                          <span className="text-xs text-gray-400">Level {p.level}</span>
                        </div>
                        <p className="font-semibold text-gray-900">{p.driver_name}</p>
                        <p className="text-sm text-gray-600 mt-1">{p.action_required}</p>
                        {p.notes && <p className="text-xs text-gray-400 mt-1">Notes: {p.notes}</p>}
                        <p className="text-xs text-gray-400 mt-1">
                          Created: {p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : '—'}
                          {p.completed_by && <> &bull; Completed by {p.completed_by} on {new Date(p.completed_at).toLocaleDateString('en-GB')}</>}
                        </p>
                      </div>
                      {p.status === 'outstanding' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleProcedureAction(p.id, 'completed')}
                            className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                          >Complete</button>
                          <button
                            onClick={() => handleProcedureAction(p.id, 'cancelled')}
                            className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                          >Cancel</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {procedures.length === 0 && <p className="text-gray-400 text-center py-8">No procedures</p>}
              </div>
            )}

            {/* Infringement Letters Tab */}
            {tab === 'letters' && (
              <div className="space-y-3">
                {letters.map((l: any) => (
                  <div key={l.id} className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-gray-400">{l.ref}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            l.status === 'acknowledged' ? 'bg-green-100 text-green-800' :
                            l.status === 'overdue' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>{l.status}</span>
                        </div>
                        <p className="font-semibold text-gray-900">{l.driver_name}</p>
                        <p className="text-sm text-gray-500">
                          Period: {l.period_start} to {l.period_end} &bull; {l.infringement_count} infringement{l.infringement_count !== 1 ? 's' : ''}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Sent by {l.sent_by} on {l.letter_date}
                          {l.email_sent && <> &bull; Emailed to {l.email_sent_to}</>}
                          {l.acknowledged_at && <> &bull; Acknowledged: {new Date(l.acknowledged_at).toLocaleDateString('en-GB')}</>}
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          setDownloadingPdf(l.id);
                          try {
                            await api.downloadLetterPdf(l.id, `${l.ref}_${l.driver_name?.replace(/\s+/g, '_')}.pdf`);
                          } catch (err: any) {
                            alert(`PDF download failed: ${err.message}`);
                          } finally {
                            setDownloadingPdf(null);
                          }
                        }}
                        disabled={downloadingPdf === l.id}
                        className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50"
                        title="Download PDF"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {downloadingPdf === l.id ? '...' : 'PDF'}
                      </button>
                    </div>
                  </div>
                ))}
                {letters.length === 0 && <p className="text-gray-400 text-center py-8">No infringement letters sent yet</p>}
              </div>
            )}

            {/* DCRS Config Tab */}
            {tab === 'config' && config && (
              <div className="bg-white border border-gray-200 rounded p-6 max-w-2xl">
                <h3 className="font-semibold text-gray-900 mb-4">DCRS Configuration</h3>

                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Level Thresholds (points to advance)</h4>
                  <div className="grid grid-cols-6 gap-2">
                    {config.level_thresholds?.map((pts: number, i: number) => (
                      <div key={i} className="text-center bg-gray-50 rounded p-3 border border-gray-200">
                        <p className="text-xs text-gray-500">L{i + 1} → L{i + 2}</p>
                        <p className="text-lg font-bold text-gray-900">{pts}</p>
                        <p className="text-[10px] text-gray-400">points</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Clean Period to Demote</h4>
                  <p className="text-sm text-gray-600">{config.clean_months_to_demote} months with no infringements = drop one level</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Points per Infringement Type</h4>
                  <div className="space-y-1">
                    {Object.entries(config.infringement_points || {}).map(([type, pts]) => (
                      <div key={type} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{type.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-semibold text-gray-900">{pts as number} pts</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Procedure Actions per Level</h4>
                  <div className="space-y-1">
                    {Object.entries(config.procedure_actions || {}).map(([level, action]) => (
                      <div key={level} className="flex items-center gap-3 py-1.5 px-3 bg-gray-50 rounded">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded border ${LEVEL_COLOURS[Number(level)] || ''}`}>L{level}</span>
                        <span className="text-sm text-gray-700">{action as string}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-red-700 mb-2">Reset All DCRS Records</h4>
                  <p className="text-xs text-gray-500 mb-3">Resets all driver points and levels back to Level 1 with 0 points. Use after clearing old data.</p>
                  <button
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to reset ALL driver DCRS records? This cannot be undone.')) {
                        try {
                          await api.resetAllDCRS();
                          await loadData();
                          alert('All DCRS records have been reset.');
                        } catch (err: any) {
                          alert('Failed to reset: ' + err.message);
                        }
                      }
                    }}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reset All Records
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Letter preview modal */}
        {showLetterModal && letterPreview && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Infringement Letter Preview</h3>
              <p className="text-sm text-gray-600 mb-2">
                <strong>{letterPreview.driver_name}</strong> — {letterPreview.count} infringement{letterPreview.count !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-400 mb-4">Period: {letterPreview.period_start} to {letterPreview.period_end}</p>

              <div className="space-y-2 mb-6">
                {letterPreview.infringements?.map((inf: any, i: number) => (
                  <div key={i} className="bg-gray-50 rounded p-3 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">{inf.date}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        inf.severity === 'major' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{inf.severity}</span>
                    </div>
                    <p className="text-sm text-gray-900 mt-1">{inf.type.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-500">{inf.description}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-end">
                <button onClick={() => { setShowLetterModal(false); setLetterPreview(null); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                <button onClick={handleSendLetter}
                  className="px-4 py-2 text-sm bg-slate-700 text-white rounded hover:bg-slate-800">Send Letter & Email</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
